import React, { useState, useEffect, useCallback } from 'react';
import { FormTemplate, FormResponses, calculateProgress } from '@/lib/forms/schema';
import { FormSectionRenderer } from './FormSectionRenderer';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Loader2, Save, Send, CheckCircle2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useDebounce } from '@/hooks/useDebounce';

interface DynamicFormRendererProps {
  template: FormTemplate;
  initialResponses?: FormResponses;
  onSave?: (responses: FormResponses) => Promise<void>;
  onSubmit?: (responses: FormResponses) => Promise<void>;
  disabled?: boolean;
  autoSave?: boolean;
  autoSaveDelay?: number;
  showProgress?: boolean;
  className?: string;
}

export function DynamicFormRenderer({
  template,
  initialResponses = {},
  onSave,
  onSubmit,
  disabled = false,
  autoSave = true,
  autoSaveDelay = 2000,
  showProgress = true,
  className,
}: DynamicFormRendererProps) {
  const [responses, setResponses] = useState<FormResponses>(initialResponses);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  const debouncedResponses = useDebounce(responses, autoSaveDelay);

  const progress = calculateProgress(template, responses);

  // Auto-save when responses change
  useEffect(() => {
    if (autoSave && hasChanges && onSave && !isSubmitting) {
      handleAutoSave();
    }
  }, [debouncedResponses]);

  const handleAutoSave = async () => {
    if (!onSave || isSaving) return;
    
    setIsSaving(true);
    try {
      await onSave(responses);
      setLastSaved(new Date());
      setHasChanges(false);
    } catch (error) {
      console.error('Auto-save failed:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleChange = useCallback((key: string, value: any) => {
    setResponses((prev) => ({ ...prev, [key]: value }));
    setHasChanges(true);
    // Clear error when field is changed
    if (errors[key]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  }, [errors]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    template.sections.forEach((section) => {
      section.fields.forEach((field) => {
        // Check if field should be validated (visible based on conditionals)
        if (field.conditional) {
          const { field: condField, operator = 'equals', value: condValue } = field.conditional;
          const currentValue = responses[condField];
          
          let isVisible = false;
          switch (operator) {
            case 'equals':
              isVisible = currentValue === condValue;
              break;
            case 'notEquals':
              isVisible = currentValue !== condValue;
              break;
            default:
              isVisible = currentValue === condValue;
          }
          
          if (!isVisible) return;
        }

        // Required validation
        if (field.required) {
          const value = responses[field.key];
          if (value === undefined || value === null || value === '' || 
              (typeof value === 'object' && Object.keys(value).length === 0)) {
            newErrors[field.key] = 'Este campo é obrigatório';
          }
        }

        // Type-specific validation
        if (field.validation && responses[field.key]) {
          const value = responses[field.key];
          
          if (field.validation.minLength && typeof value === 'string' && value.length < field.validation.minLength) {
            newErrors[field.key] = `Mínimo de ${field.validation.minLength} caracteres`;
          }
          
          if (field.validation.maxLength && typeof value === 'string' && value.length > field.validation.maxLength) {
            newErrors[field.key] = `Máximo de ${field.validation.maxLength} caracteres`;
          }

          if (field.validation.pattern && typeof value === 'string') {
            const regex = new RegExp(field.validation.pattern);
            if (!regex.test(value)) {
              newErrors[field.key] = field.validation.message || 'Formato inválido';
            }
          }
        }
      });
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      // Scroll to first error
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        const element = document.querySelector(`[data-field-key="${firstErrorKey}"]`);
        element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    if (!onSubmit) return;

    setIsSubmitting(true);
    try {
      await onSubmit(responses);
    } catch (error) {
      console.error('Submit failed:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleManualSave = async () => {
    if (!onSave) return;
    
    setIsSaving(true);
    try {
      await onSave(responses);
      setLastSaved(new Date());
      setHasChanges(false);
    } catch (error) {
      console.error('Save failed:', error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className={cn('space-y-6', className)}>
      {/* Header with progress */}
      {showProgress && template.settings.showProgress && (
        <div className="sticky top-0 z-10 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 py-4 border-b">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Progresso</span>
            <span className="text-sm text-muted-foreground">{progress}% completo</span>
          </div>
          <Progress value={progress} className="h-2" />
          
          {/* Save status */}
          <div className="flex items-center justify-between mt-2">
            <div className="text-xs text-muted-foreground">
              {isSaving && (
                <span className="flex items-center gap-1">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Salvando...
                </span>
              )}
              {!isSaving && lastSaved && (
                <span className="flex items-center gap-1">
                  <CheckCircle2 className="h-3 w-3 text-green-500" />
                  Salvo às {lastSaved.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                </span>
              )}
              {!isSaving && hasChanges && !lastSaved && (
                <span>Alterações não salvas</span>
              )}
            </div>
            
            {onSave && template.settings.allowSaveDraft && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleManualSave}
                disabled={isSaving || !hasChanges}
              >
                <Save className="h-4 w-4 mr-1" />
                Salvar rascunho
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Form sections */}
      <div className="space-y-6">
        {template.sections.map((section) => (
          <FormSectionRenderer
            key={section.key}
            section={section}
            responses={responses}
            onChange={handleChange}
            errors={errors}
            disabled={disabled || isSubmitting}
          />
        ))}
      </div>

      {/* Submit button */}
      {onSubmit && (
        <div className="flex justify-end pt-4 border-t">
          <Button
            type="submit"
            size="lg"
            disabled={isSubmitting || disabled}
            className="min-w-[200px]"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Enviando...
              </>
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                Enviar Formulário
              </>
            )}
          </Button>
        </div>
      )}
    </form>
  );
}
